// Tabung: the list, its filters, the cylinder page, its cards and drawers, the status dialogs.
// Formal and calm: no exclamation marks; "Titik Distribusi" and "Reader" in every sentence (API ids stay English).
import type { CylinderStatus, PositionStatus } from '@lib/api/common';
import type { NotSeenFor, TestState } from '@lib/api/cylinders';

export const CYLINDERS = {
  title: 'Tabung',
  sub: 'Tabung terdaftar beserta posisi, isi, dan jadwal uji berkala.',
  noun: 'tabung',
  loading: 'Memuat tabung…',
  results: 'Daftar tabung',
} as const;

/** Where a cylinder is, as this page says it (the badge and the header sentence). */
export const CYLINDER_POSITION: Record<PositionStatus, string> & { warehouse: string } = {
  AT_LOCATION: 'Di Titik Distribusi',
  IN_TRANSIT: 'Dalam pengiriman',
  MISSING: 'Tidak ditemukan',
  WITH_CONSUMER: 'Di warga',
  WITH_COURIER: 'Dibawa kurir ke Hub',
  warehouse: 'Di gudang',
};

/** The status tabs: what each shows, as laptops and phones say it. */
export const CYLINDER_TABS = {
  label: 'Status tabung',
  all: 'Semua',
  at: 'Di Titik Distribusi',
  transit: 'Dalam pengiriman',
  missing: 'Tidak ditemukan',
  test: 'Perlu uji berkala',
  inactive: 'Nonaktif',
  /** the tab's accessible name with its count */
  name: (label: string, n: string) => `${label}, ${n} tabung`,
} as const;

export const CYLINDER_FILTERS = {
  dormant: 'Lama tidak ditukar',
  search: 'Cari tabung',
  searchPlaceholder: 'Cari nomor seri lengkap, angka nomor seri, atau awal EPC',
  searchPlaceholderShort: 'Nomor seri atau awal EPC',
  /** fewer characters than the search accepts: said under the box, the list stays as it is */
  searchShort: (min: number) => `Ketik minimal ${min} karakter untuk mencari.`,
  region: 'Wilayah',
  place: 'Titik Distribusi',
  placeType: 'Jenis',
  typesChosen: (n: number) => `${n} jenis`,
  placeSearch: 'Cari Titik Distribusi',
  allPlaces: 'Semua Titik Distribusi',
  placesEmpty: 'Tidak ada Titik Distribusi yang cocok',
  placesFailed: 'Daftar Titik Distribusi gagal dimuat.',
  morePlaces: (n: number) => `${n} teratas. Ketik nama untuk mencari yang lain.`,
  searching: 'Mencari…',
  test: 'Uji berkala',
  allTests: 'Semua jadwal',
  overdue: 'Lewat jadwal',
  soon: (days: number) => `Jatuh tempo ≤ ${days} hari`,
  seen: 'Terakhir terbaca',
  allSeen: 'Kapan saja',
  seenOption: { '7d': 'Lebih dari 7 hari', '30d': 'Lebih dari 30 hari', '90d': 'Lebih dari 90 hari', never: 'Belum pernah terbaca' } as Record<NotSeenFor, string>,
  seenActiveOnly: 'Hanya tabung aktif.',
  fill: 'Isi tabung',
  allFill: 'Semua',
  fillHelp: 'Status isi tercatat saat tabung diberangkatkan dengan Surat Jalan.',
  seenChip: { '7d': 'Tidak terbaca > 7 hari', '30d': 'Tidak terbaca > 30 hari', '90d': 'Tidak terbaca > 90 hari', never: 'Belum pernah terbaca' } as Record<NotSeenFor, string>,
  clearChoice: 'Kosongkan pilihan',
} as const;

export const CYLINDER_EMPTY = {
  none: { title: 'Belum ada tabung terdaftar', text: (warehouse: string) => `Tabung terdaftar saat tag-nya dibaca Reader di ${warehouse}, lalu tampil di sini.` },
  noMatch: 'Tidak ada tabung yang cocok. Ubah kata kunci atau filter.',
  /** a search finds whole serial numbers, their number, or the start of an EPC: never a fragment */
  noMatchSearch: 'Tidak ada tabung yang cocok. Ketik nomor seri lengkap (CNG-0001234), angka nomor serinya (1234), atau minimal 4 karakter awal EPC.',
  /** the order some filters bring (there is no sort menu) */
  orderTest: 'Diurutkan dari jadwal uji berkala terdekat.',
  orderSeen: 'Diurutkan dari yang paling lama tidak terbaca.',
  tab: 'Tidak ada tabung di kelompok ini.',
  /** the search was too broad to answer in time (SEARCH_TOO_BROAD) */
  tooBroad: 'Pencarian terlalu luas untuk dijawab. Tambahkan filter Wilayah atau Titik Distribusi, atau hapus filter Terakhir terbaca.',
  clearSeen: 'Hapus filter Terakhir terbaca',
} as const;

export const CYLINDER_COLUMNS = {
  serial: 'Nomor seri',
  position: 'Posisi',
  test: 'Uji berkala',
  seen: 'Terakhir terbaca',
  status: 'Status',
} as const;

/** The retest: a badge only when it needs a look. */
export const TEST_STATE: Record<TestState, string> = { none: 'Belum dijadwalkan', ok: 'Sesuai jadwal', soon: 'Segera jatuh tempo', overdue: 'Lewat jadwal' };

export const CYLINDER_ROW = {
  never: 'Belum pernah terbaca',
  reader: (label: string) => `Reader ${label}`,
  menu: (serial: string) => `Tindakan untuk ${serial}`,
  open: 'Lihat rincian',
  edit: 'Ubah data',
} as const;

/** Status changes: each with a required reason and what it means for the cylinder. */
export const CYLINDER_STATUS_ACTION: Record<Exclude<CylinderStatus, 'DESTROYED'>, { label: string; title: (serial: string) => string; consequence: string; done: (serial: string) => string }> = {
  DAMAGED: {
    label: 'Tandai rusak',
    title: (s) => `Tandai ${s} rusak`,
    consequence: 'Tabung tidak dapat dimuat ke Surat Jalan sampai diaktifkan kembali. Posisi dan riwayatnya tetap tercatat.',
    done: (s) => `${s} ditandai rusak`,
  },
  WITHDRAWN: {
    label: 'Tarik dari peredaran',
    title: (s) => `Tarik ${s} dari peredaran`,
    consequence: 'Tabung dikeluarkan dari peredaran dan tidak dapat dimuat ke Surat Jalan. Riwayatnya tetap tersimpan.',
    done: (s) => `${s} ditarik dari peredaran`,
  },
  LOST: {
    label: 'Nyatakan hilang',
    title: (s) => `Nyatakan ${s} hilang`,
    consequence: 'Tabung dicatat hilang dan tidak dapat dimuat ke Surat Jalan sampai ditemukan dan diaktifkan kembali. Riwayatnya tetap tersimpan.',
    done: (s) => `${s} dinyatakan hilang`,
  },
  ACTIVE: {
    label: 'Aktifkan kembali',
    title: (s) => `Aktifkan kembali ${s}`,
    consequence: 'Tabung dapat dimuat ke Surat Jalan lagi. Pastikan tabung sudah diperiksa dan layak edar.',
    done: (s) => `${s} aktif kembali`,
  },
};

export const CYLINDER_STATUS_DIALOG = {
  reason: 'Alasan',
  reasonHelp: (min: number, max: number) => `${min}–${max} karakter. Tersimpan di Log aktivitas.`,
  reasonRequired: 'Tulis alasan perubahan status.',
  reasonShort: (min: number) => `Alasan minimal ${min} karakter.`,
  /** reactivating a cylinder whose retest has passed */
  testOverdue: 'Jadwal uji berkala tabung ini sudah lewat: tabung tetap tidak dapat dimuat sampai uji berkala dicatat di Ubah data.',
} as const;

export const CYLINDER_DELETE = {
  title: (serial: string) => `Hapus ${serial}?`,
  body: 'Hanya untuk kesalahan pendaftaran. Tabung yang keluar dari peredaran sebaiknya ditarik dari peredaran. Tabung yang sudah tercantum di Surat Jalan tidak dapat dihapus.',
  confirm: 'Hapus tabung',
  label: 'Hapus',
  done: (serial: string) => `${serial} dihapus`,
  /** why Hapus is not offered for a cylinder that was ever shipped (`everShipped`) */
  shipped: 'Tabung sudah pernah dikirim; hanya salah registrasi yang bisa dihapus. Gunakan Tarik dari peredaran.',
  /** the server's refusal of the same (409 CYLINDER_IN_HISTORY) */
  inHistory: 'Tabung sudah pernah dikirim; hanya salah registrasi yang bisa dihapus. Tarik tabung dari peredaran sebagai gantinya.',
} as const;

/** Ganti tag (admins, cylinder.retag): the cylinder gets a new tag read at its Titik Distribusi; the old one is retired. */
export const CYLINDER_RETAG = {
  label: 'Ganti tag',
  title: (serial: string) => `Ganti tag ${serial}`,
  /** why the menu item is not available now, by where the cylinder is */
  blocked: {
    IN_TRANSIT: 'Tabung sedang dalam pengiriman; tag dapat diganti setelah diterima',
    MISSING: 'Tabung tercatat tidak sampai; tag dapat diganti setelah ditemukan',
    ON_SHIPMENT: 'Tabung sedang dimuat ke Surat Jalan; tag dapat diganti setelah keluar dari muatan',
  },
  consequence: 'Tag lama tidak bisa dipakai lagi.',
  consequenceMore: 'Tag lama dinonaktifkan selamanya; tabung hanya dikenali dengan tag baru.',
  oldTag: 'Tag lama',
  confirmTitle: (serial: string) => `Ganti tag ${serial}?`,
  confirmBody: (oldEpc: string, newEpc: string) => `Tag lama ${oldEpc} diganti dengan ${newEpc} dan tidak dapat dipakai lagi. Tindakan ini tidak dapat dibatalkan.`,
  /** the cylinder left its Titik Distribusi while the dialog was open */
  nowBlocked: 'Tabung tidak lagi berada di Titik Distribusi, sehingga tag tidak dapat diganti sekarang.',
  unusableNote: 'Tidak lagi dapat dipakai',
  goneStale: 'Tag ini tidak lagi terbaca. Dekatkan kembali ke Reader atau pilih tag lain.',
  intro: (place: string) => `Dekatkan tag baru ke Reader di ${place}. Hanya tag yang belum pernah dipakai dan ID chip (TID)-nya sudah terbaca yang muncul di sini.`,
  tags: 'Tag baru',
  required: 'wajib dipilih',
  loading: 'Mencari tag di depan Reader…',
  empty: (place: string) => `Belum ada tag baru yang terbaca di ${place}. Dekatkan tag baru ke Reader; daftar ini diperbarui otomatis.`,
  others: (n: number) => `${n} tag lain terbaca tetapi tidak dapat dipakai: sudah terpasang, pernah dipakai, atau ID chip (TID) belum terbaca.`,
  tid: (tid: string) => `TID ${tid}`,
  gone: 'Sudah tidak di depan Reader',
  tagRequired: 'Pilih tag baru yang terbaca di Reader.',
  tagUnusable: 'Tag ini tidak lagi dapat dipakai. Pilih tag lain.',
  reasonRequired: 'Tulis alasan penggantian tag.',
  submit: 'Ganti tag',
  done: (serial: string) => `Tag ${serial} sudah diganti`,
  /** refusals of POST /cylinders/:id/retag, by code (the tag refusals are TAG_ERRORS) */
  errors: {
    CYLINDER_IN_TRANSIT: 'Tabung sedang dalam pengiriman. Tag dapat diganti setelah tabung diterima di Titik Distribusi.',
    CYLINDER_MISSING: 'Tabung tercatat tidak sampai. Tag dapat diganti setelah tabung ditemukan di sebuah Titik Distribusi.',
    CYLINDER_ON_SHIPMENT: 'Tabung sedang dimuat ke Surat Jalan. Tag dapat diganti setelah tabung dikeluarkan dari muatan atau diterima.',
  } as Record<string, string>,
} as const;

export const CYLINDER_FOUND = {
  body: (place: string) => `Tabung dicatat di ${place}, sesuai pembacaan Reader terakhir.`,
  place: 'Titik Distribusi terakhir terbaca',
  /** why it cannot be marked found yet */
  notSeen: 'Tandai ditemukan tersedia setelah tabung terbaca lagi oleh Reader di sebuah Titik Distribusi.',
} as const;

/** The cylinder page: its header, the cards and the drawers behind "Lihat semua". */
export const CYLINDER_DETAIL = {
  missing: 'Tabung tidak ditemukan',
  back: 'Kembali ke daftar tabung',
  epcMissing: 'Tag belum terdaftar',
  more: 'Tindakan lainnya',
  edit: 'Ubah data',
  demo: 'Data simulasi. Tabung ini dibuat untuk demo.',
  clone: (tid: string) => `TID chip yang terakhir terbaca (${tid}) berbeda dengan yang terdaftar. Kemungkinan tag palsu atau tag ditukar.`,
  /** the header sentence */
  /** "Di {Titik Distribusi} sejak 24 Okt 2026" (the place is a link) */
  atLead: 'Di',
  atSince: (since: string) => `sejak ${since}`,
  /** "Dalam pengiriman ke {tujuan}, Surat Jalan {nomor}" (the number is a link) */
  inTransitLead: (to: string | null) => (to ? `Dalam pengiriman ke ${to}, Surat Jalan` : 'Dalam pengiriman, Surat Jalan'),
  inTransitNoDoc: 'Dalam pengiriman',
  missingSince: (since: string) => `Tidak ditemukan sejak ${since}`,
  missingNoDate: 'Tidak ditemukan',
  /** a household holds it (Phase 3: the exchange at the door) */
  withConsumer: 'Di warga, diserahkan lewat tukar tabung',
  /** DESTROYED is final: nothing about the cylinder changes any more */
  destroyed: 'Tabung ini sudah dimusnahkan setelah gagal uji berkala. Datanya tidak dapat diubah lagi dan tagnya tidak dapat dipakai tabung lain.',
  seeAll: (n: string) => `Lihat semua (${n})`,
  seeAllPlain: 'Lihat semua',
  editBlocked: {
    away: 'Tabung sedang dalam pengiriman atau tidak ditemukan: hanya admin yang dapat mengubah datanya.',
    elsewhere: 'Tabung ini tercatat di Titik Distribusi lain. Petugas hanya dapat mengubah tabung di Titik Distribusi tugasnya.',
  },
  identity: {
    title: 'Identitas',
    serial: 'Nomor seri',
    epc: 'EPC',
    tid: 'TID',
    tidNone: 'Belum terbaca',
    kind: 'Jenis dan kapasitas',
    fill: 'Isi tabung',
    manufacturer: 'Produsen',
    manufacturedOn: 'Tanggal produksi',
    lastTest: 'Uji berkala terakhir',
    nextTest: 'Uji berkala berikutnya',
    owner: 'Pemilik',
    notes: 'Catatan',
    registered: 'Didaftarkan',
    region: 'Wilayah',
    chip: 'Keaslian chip',
    chipMore: 'Rincian chip',
    lastReadTid: 'TID terakhir terbaca',
    chipHelp: 'TID adalah nomor bawaan pabrik chip dan tidak dapat diubah. Bila EPC ini terbaca dengan TID lain, sistem menandainya sebagai indikasi tag ganda dan mengirim peringatan.',
    chipState: { clone: 'Indikasi tag ganda', match: 'Asli (TID cocok)', registered: 'TID terdaftar', unread: 'TID belum terbaca' },
  },
  journey: {
    title: 'Perjalanan',
    noun: 'langkah',
    registered: (place: string) => `Didaftarkan di ${place}`,
    received: (place: string) => `Diterima di ${place}`,
    from: (place: string) => `dari ${place}`,
    elsewhere: 'Titik Distribusi lain',
    empty: 'Belum ada perjalanan.',
    drawerTitle: (serial: string) => `Perjalanan ${serial}`,
  },
  route: {
    title: 'Rute di peta',
    hint: (n: number) => `${n} Titik Distribusi`,
    label: (serial: string) => `Rute ${serial}`,
    empty: 'Belum ada Titik Distribusi dengan titik peta.',
  },
  reads: {
    title: 'Pembacaan Reader',
    noun: 'pembacaan',
    empty: 'Belum pernah terbaca oleh Reader.',
    emptyDemo: 'Tabung simulasi hanya memiliki riwayat kiriman simulasi.',
    unplaced: 'Belum ditempatkan',
    load: (n: string) => `Muat ${n}`,
    unload: (n: string) => `Terima ${n}`,
    sighting: 'Terbaca di Titik Distribusi',
    dwell: (s: number) => `${s} dtk`,
    drawerTitle: (serial: string) => `Pembacaan Reader ${serial}`,
    present: 'Sedang di depan Reader',
  },
  shipments: {
    title: 'Surat Jalan',
    noun: 'Surat Jalan',
    route: (from: string, to: string) => `${from} → ${to}`,
    empty: 'Belum pernah dimuat ke Surat Jalan.',
    received: 'Diterima',
    missing: 'Tidak sampai',
    drawerTitle: (serial: string) => `Surat Jalan ${serial}`,
  },
  alerts: {
    title: 'Peringatan aktif',
    empty: 'Tidak ada peringatan aktif.',
  },
  tests: {
    title: 'Riwayat uji',
    noun: 'hasil uji',
    empty: 'Belum ada hasil uji dari Bengkel Uji.',
    drawerTitle: (serial: string) => `Riwayat uji ${serial}`,
    nextDue: (day: string) => `Uji berikutnya ${day}`,
    reason: (text: string) => `Alasan: ${text}`,
    by: (name: string) => `oleh ${name}`,
  },
} as const;

/** Ubah data: everything but the serial number (issued) and the status (its own actions). */
export const CYLINDER_EDIT = {
  title: (serial: string) => `Ubah data ${serial}`,
  save: 'Simpan',
  saved: 'Data tabung disimpan',
  serial: 'Nomor seri',
  serialHelp: 'Dibuat otomatis saat pendaftaran dan tidak dapat diubah.',
  kindPlaceholder: 'CNG Type 4',
  fields: {
    kind: 'Jenis',
    capacity: 'Kapasitas',
    manufacturer: 'Produsen',
    manufacturerSerial: 'Nomor seri pabrik',
    manufacturedOn: 'Tanggal produksi',
    lastTestOn: 'Uji berkala terakhir',
    nextTestDue: 'Uji berkala berikutnya',
    owner: 'Pemilik',
    notes: 'Catatan',
  },
  errors: {
    makerSerialTooLong: 'Paling banyak 60 karakter.',
    kindRequired: 'Jenis wajib diisi.',
    lastBeforeMade: 'Uji berkala terakhir tidak dapat sebelum tanggal produksi.',
    nextBeforeMade: 'Uji berkala berikutnya harus setelah tanggal produksi.',
    nextBeforeLast: 'Uji berkala berikutnya harus setelah uji berkala terakhir.',
  },
} as const;
